
import { Box, Card, Checkbox, FormControlLabel, MenuItem, Pagination, Stack, Typography, useTheme } from "@mui/material";
import Enumerable from "linq";
import _ from "lodash";
import React, { forwardRef, lazy, Ref, useEffect, useImperativeHandle, useMemo, useState } from "react";
import { isMobile, isTablet } from 'react-device-detect';
import { useTranslation } from "react-i18next";
import { AccessType } from "../../../common/enums/DRCXEnums";
import { UIRefreshCategoryEnum } from "../../../common/enums/UIRefreshCategoryEnum";
import { IeLynxObject } from "../../../common/models/eLynxObject";
import { getAccessText } from "../../../common/utilities/GetAccessText";
import AccessLevelDialog, { bindAccessLevelDialog, useAccessLevelDialogState } from "../../../components/access-level-dialog/AccessLevelDialog";
import { ActionMenuItem, BaseActionEnum } from "../../../components/action-menu/ActionMenuItem";
import { ContentContainer } from "../../../components/content-container/ContentContainer";
import { ContentSection } from "../../../components/content-section/ContentSection";
import { ViewOptionStateEnum } from "../../../components/contextual-app-bar/ViewOptionStateEnum";
import DefaultConfigurationDialog, { bindDefaultConfigurationDialog, useDefaultConfigurationDialogState } from "../../../components/default-configuration-dialog/DefaultConfigurationDialog";
import LoadingZone from "../../../components/loading-zone/LoadingZone";
import NoData from "../../../components/no-data/NoData";
import OptionsSideSheet from "../../../components/options-side-sheet/OptionsSideSheet";
import RenameConfigurationDialog, { bindRenameConfigurationDialog, useRenameConfigurationDialogState } from "../../../components/rename-configuration-dialog/RenameConfigurationDialog";
import SideSheetFilterInput from "../../../components/side-sheet/side-sheet-filter-input/SideSheetFilterInput";
import SideSheetGroupedSelect from "../../../components/side-sheet/side-sheet-grouped-select/SideSheetGroupedSelect";
import SideSheetSelect from "../../../components/side-sheet/side-sheet-select/SideSheetSelect";
import { AppNotificationTypeEnum } from "../../../contexts/app-notification-context/AppNotificationContextTypes";
import { useAppNotificationContext } from "../../../contexts/app-notification-context/UseAppNotificationContext";
import { useConfirmation } from "../../../contexts/confirmation-context/ConfirmationContext";
import { useOfflineDetectContext } from "../../../contexts/offline-detect-context/UseOfflineDetectContext";
import { useUserContext } from "../../../contexts/user-context/UserContext";
import { useRouteContext } from "../../../hooks/use-route-context/UseRouteContext";
import { DEFAULT_REFRESH_SECONDS } from "../../../queries/BaseUseQueryParameters";
import { useDeviceSummaryConfigurations } from "../../../queries/group-summary/devices/UseDeviceSummaryConfigurations";
import { useDeviceSummaryData } from "../../../queries/group-summary/devices/UseDeviceSummaryData";
import { useDeviceGroupByAttributeData } from "../../../queries/group-summary/devices/UseDeviceSummaryGroupByAttributeData";
import { useObjectViewState } from "../../../queries/object-view-state/UseObjectViewState";
import { useDeletePageConfiguration } from "../../../queries/page-configuration/UseDeletePageConfiguration";
import { useModifyPageConfigurationAccess } from "../../../queries/page-configuration/UseModifyPageConfigurationAccess";
import { usePageConfiguration } from "../../../queries/page-configuration/UsePageConfiguration";
import { useRenamePageConfiguration } from "../../../queries/page-configuration/UseRenamePageConfiguration";
import { useSavePageConfigurationDefault } from "../../../queries/page-configuration/UseSavePageConfiguationDefault";
import { useSavePageConfiguration } from "../../../queries/page-configuration/UseSavePageConfiguration";
import { useQueryNotifications } from "../../../queries/UseQueryNotifications";
import { PageConfigurationAccessEnum, PageConfigurationModel4, PageConfigurationTypeEnum } from "../../../services/page-configuration-service/Models";
import PageConfigurationService from "../../../services/page-configuration-service/PageConfigurationService";
import { RestError } from "../../../services/rest-service/RestService";
import { IGroupViewState } from "../../../services/state-data-service/Models";
import { ISummaryListingExportParams } from "../../../services/summary-listing-data-service/BaseModels";
import { bindPageConfigurationEditorDialog, usePageConfigurationEditorDialogState } from "../configuration-editor/PageConfigurationEditorDialogState";
import { PageConfigurationEditorDialogResult } from "../configuration-editor/PageConfigurationEditorDialog";
import { actionItems } from "../SummaryListingActions";
import { useSummaryListingContext } from "../SummaryListingContext";
import SummaryListingViewUtils, { ACCESS_LEVEL_MODIFY_CONFIRMATION_PARAMS, RENAME_CONFIGURATION_DIALOG_PARAMS, writePermission } from "../SummaryListingViewUtils";
import useDevicesViewState, { DevicesViewActionEnum } from "./DevicesViewState";
import { createGridData, createViewOptions, getAttributeOptions, getDefaultConfigurationID, GridSectionData, groupDataByAttribute, GroupedGridSection, hasDeprecatedColumns, PageConfigurationSelectOption, pageSize } from "./DevicesViewUtils";
import SectionGrid from "./SectionGrid";
import { useUserFavoritesContext } from "../../../contexts/user-favorites-context/UserFavoritesContext";
import { UIViewEnum } from "../../../common/enums/UIViewEnum";
import { NavigationSource } from "../../../common/enums/NavigationSource";
import { SummaryListingSortTypeEnum } from "../../../common/enums/SummaryListingSortTypeEnum";
import { useUserDefaultViews } from "../../../queries/user-default-view/UseUserDefaultViews";
import { eLynxObjectTypeEnum } from "../../../common/enums/eLynxObjectType";
import { routesByViewID } from "../../../routes/routes";
import { hasPermission } from "../../../common/utilities/HasPermission";
import { IApiProblemDetails } from "../../../common/models/IApiProblemDetails";
import { useEvaluateFeature } from "../../../queries/features/UseEvaluateFeature";
import { FeatureConstants } from "../../../services/features-data-service/Models";
import { useConfigurationsContext } from "../../../contexts/configurations-context/UseConfigurationsContext";
import { subscribe, unsubscribe } from "pubsub-js";
import { CONFIGURATION_CHANGED_TOPIC, ConfigurationChangedMsg } from "../../../common/models/pub-sub/PubSubTopics";
import SideSheetCheckbox from "../../side-sheet/side-sheet-checkbox/SideSheetCheckbox";
//import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
//import { faChartGantt } from "@elynx/free-solid-svg-icons";
import { useUserSettings } from "../../../queries/user-settings/UseUserSettings";
import { UIShortcutEnum } from "../../../services/user-settings-data/Models";

const PageConfigurationEditorDialog = lazy(() => import("../configuration-editor/PageConfigurationEditorDialog"));

export type DevicesViewProps = {
    visible?: boolean;
    setViewOptionState?: (state: ViewOptionStateEnum) => void;


    groupID: number,
    contextObject: IeLynxObject,
    configurationID?: number,
    dense?: boolean;
    designMode?: boolean;
};

export type DevicesViewType = {
    executeAction: (action: BaseActionEnum | string) => void;
    getExportParameters: () => ISummaryListingExportParams;
};

function DevicesView({
    visible,
    setViewOptionState,
    groupID,
    contextObject,
    configurationID,
    dense,
    designMode

}: DevicesViewProps, ref: Ref<unknown> | undefined) {

    const [data, setData] = useState<GridSectionData[]>([]);

    const { t } = useTranslation();
    const routeContext = useRouteContext();
    const [offlineState] = useOfflineDetectContext();
    const { setActions, setDeviceCount } = useSummaryListingContext();
    const [userState] = useUserContext();
    const renameConfigurationDialog = useRenameConfigurationDialogState();
    const defaultConfigurationDialog = useDefaultConfigurationDialogState();    
    const accessLevelDialog = useAccessLevelDialogState();    
    const [, setAppNotificationState] = useAppNotificationContext();
    const { confirm, acknowledge } = useConfirmation();
    const theme = useTheme();
    const [updateFavoriteContext] = useUserFavoritesContext();
    const {
        configurationsContextState, 
        setContextConfigurations,
        setContextConfigurationID
    } = useConfigurationsContext();

    const { data: userSettings } = useQueryNotifications(useUserSettings)({
        enabled: !offlineState.offline
    });
    
    const { data: headerAndActionsV2Feature } = useQueryNotifications(useEvaluateFeature)({
        feature: FeatureConstants.HeaderAndActionsV2,
    });

    const headerAndActionsV2 = (headerAndActionsV2Feature ?? false);


    const { data: defaultViews } = useQueryNotifications(useUserDefaultViews)({
        enabled: !offlineState.offline
    });

    const {
        state,
        resetOptions,
        setConfigurationID,
        setFilterText,
        setDefaultConfigurationID,
        setNoData,
        setGroupByID,
        setPage,
        setAttributeOptions,
        setRefreshing,
        setConfigurationError,
        setFunctionIcons,
        setAlarmsAtTop,
        setOutletsFirst,
        setShowHistoricalDataShortcut,
        setShowGraphShortcut,
        setShowMarketinReportShortcut,
        setShowPlungerAnalysisShortcut,
        setShowGaugesheetShortcut,
        setShowDynacardShortcut,
        setShowAddMemoShortcut,
        setShowMapItShortcut


    } = useDevicesViewState();

    useImperativeHandle<unknown, DevicesViewType>(
        ref,
        () => ({
            executeAction: executeAction,
            getExportParameters: getExportParameters,
        })
    );

    const { data: objectViewState} = useQueryNotifications(useObjectViewState)({

        customerID: userState.customer?.id as number,
        groupID: groupID,
        contextObject: contextObject,
    });
    const groupViewState = objectViewState ? objectViewState as IGroupViewState : undefined;


    const { data: summaryConfigurations, refetch: refetchConfigurations } = useQueryNotifications(useDeviceSummaryConfigurations)({
        groupID: groupID,
        enabled: true
    });

    const configurationName = useMemo(() => {
        if (summaryConfigurations && state.configurationID !== -1) {
            const currentConfiguration = (summaryConfigurations?.privateConfigurations ?? [])
                .concat(summaryConfigurations?.sharedConfigurations ?? [])
                .find(o => o.id === state.configurationID);

            return currentConfiguration?.name ?? undefined;
        }

        return undefined;
    }, [
        summaryConfigurations, 
        state.configurationID
    ]);

    const { data: summaryData, status, refetch } = useQueryNotifications(useDeviceSummaryData)({
        customerID: userState.customer?.id as number,
        groupID: groupID,
        offline: offlineState.offline,
        configurationID: state.configurationID,
        enabled: offlineState.offline || state.configurationID !== -1,
        refreshSeconds: userState.refreshIntervals.find(o => o.category === UIRefreshCategoryEnum.SummaryListing)?.refreshSeconds ?? DEFAULT_REFRESH_SECONDS,
        onSuccess: () => {
            setTimeout(() => setRefreshing(false), 2000);

            setConfigurationError(false);
        },
        onError: (error: RestError) => {
            setRefreshing(false);

            setConfigurationError(true);

            const problemDetails = (error.response?.data as unknown) as IApiProblemDetails;
            setAppNotificationState({
                notificationType: AppNotificationTypeEnum.Error,
                title: t("summaryListing.configurationError"),
                message: t("summaryListing.configurationErrorText", { name: configurationName }),
                hideContactInfo: false,
                error: !problemDetails?.message ? error : undefined,
                show: true
            });

        }
    });

    const { data: groupByAttributeData/*, status: attributeStatus*/ } = useQueryNotifications(useDeviceGroupByAttributeData)({
        groupID: groupID,
        attributeName: summaryData?.groupByAttributes?.find(o => o.aid === state.groupByID)?.an,
        enabled: state.groupByID !== -1,
        onSuccess: () => {
            setTimeout(() => setRefreshing(false), 2000);

            setConfigurationError(false);
        },
        onError: (error: RestError) => {
            setRefreshing(false);

            setConfigurationError(true);

            const problemDetails = (error.response?.data as unknown) as IApiProblemDetails;
            setAppNotificationState({
                notificationType: AppNotificationTypeEnum.Error,
                title: t("summaryListing.configurationError"),
                message: t("summaryListing.configurationErrorText", { name: configurationName }),
                hideContactInfo: false,
                error: !problemDetails?.message ? error : undefined,
                show: true
            });

        }
    });

    const { mutate: renameConfiguration } = useQueryNotifications(useRenamePageConfiguration)({
        groupID: groupID,
        configurationID: state.configurationID,
        onSuccess: (data) => {
            acknowledge(t('configuration.renameSuccessMessage', {
                configurationType: t("configuration.types.pageConfiguration"),
                oldName: data.oldName,
                newName: data.newName
            }));
        }
    });

    const { mutate: deleteConfiguration } = useQueryNotifications(useDeletePageConfiguration)({
        groupID: groupID,
        //configurationID: state.configurationID as number,
        //pageConfigurationType: PageConfigurationTypeEnum.SummaryListing_RTS,
        onSuccess: (data) => {

            acknowledge(t('configuration.deleteSuccessMessage', {
                configurationType: t("configuration.types.pageConfiguration"),
                configurationName: data.deletedName
            }));

            setConfigurationID(summaryConfigurations?.defaultPageConfigurationId ?? -1);
        }
    });

    const {data: configurationModel/*, refetch*/} = useQueryNotifications(usePageConfiguration)({
        groupID: groupID,
        configurationID: state.configurationID ?? -1,
        pageConfigurationType: PageConfigurationTypeEnum.SummaryListing_RTS
    });


    const {mutateAsync: saveConfiguration} = useQueryNotifications(useSavePageConfiguration)({
        groupID: groupID,
        //onSuccess: (data) => {
        //}
    });


    const { mutate: setDefaultConfiguration } = useQueryNotifications(useSavePageConfigurationDefault)({
        groupID: groupID,
        pageConfigurationType: PageConfigurationTypeEnum.SummaryListing_RTS,
        onSuccess: (data) => {

            setDefaultConfigurationID(data.configurationID as number);

            let message = '';

            if (data.defaultForUser && data.defaultForAllUsers) {
                message = t('defaultConfiguration.success1');
            } else if (data.defaultForUser && data.defaultForGroup) {
                message = t('defaultConfiguration.success2');
            } else if (data.defaultForAllUsers) {
                message = t('defaultConfiguration.success3');
            } else if (data.defaultForGroup) {
                message = t('defaultConfiguration.success4');
            } else {
                message = t('defaultConfiguration.success5');
            }

            acknowledge(message);
        },
    });

    const { mutate: modifyAccess } = useQueryNotifications(useModifyPageConfigurationAccess)({
        groupID: groupID,
        configurationID: state.configurationID as number,
        onSuccess: (data) => {
            acknowledge(t("configuration.accessLevelModifySuccessMessage", {
                configurationType: t("configuration.types.pageConfiguration"),
                oldAccess: getAccessText(data.oldAccess),
                newAccess: getAccessText(data.newAccess)
            }));
        }
    });

    const configurationDialog = usePageConfigurationEditorDialogState();

    const hasGridDataToDisplay = visible && (summaryData?.gridData?.flatMap(o => o.rows)
                                            .filter(o => o.deviceName.toLowerCase().indexOf(state.filterText.trim().toLowerCase()) !== -1 || 
                                            state.filterText === '').length ?? 0) > 0;
    
    const isSystemBalance: boolean = groupViewState?.isSystemBalance ?? false;
    const sortType: SummaryListingSortTypeEnum = groupViewState?.summaryListingSortType === SummaryListingSortTypeEnum.Custom && 
                                                configurationModel?.summaryListingSortType === SummaryListingSortTypeEnum.Custom ?
                                                SummaryListingSortTypeEnum.Custom : 
                                                SummaryListingSortTypeEnum.Alphanumeric;

    const isPhone = isMobile && !isTablet;

    const noDataMessage = (summaryData?.sectionCount ?? 0) > 0 ? 
                            t("summaryListing.nodataMultiSection", { count: summaryData?.deviceCount }) : 
                            t("summaryListing.nodata", { objectType: 'devices' });
    const noDataFilterMessage = t("summaryListing.nodataFilter", { objectType: 'devices' });

    
    //const defaultConfigID = summaryConfigurations?.defaultPageConfigurationId ?? -1;
    const defaultConfigID = getDefaultConfigurationID(summaryConfigurations, isSystemBalance);

    const sharedConfigurations = useMemo(() => {
        return summaryConfigurations && !offlineState.offline
        ? createViewOptions(summaryConfigurations?.sharedConfigurations?.filter(o => (o.sectionCount ?? 0) < 2 || !isSystemBalance),
            PageConfigurationAccessEnum.Shared,
            defaultConfigID,
            false
            )
        : new Array<PageConfigurationSelectOption>();
    }, [
        summaryConfigurations,
        offlineState.offline,
        isSystemBalance,
        defaultConfigID
    ]);

    const sharedReadOnlyConfigurations = useMemo(() => {
        return summaryConfigurations && !offlineState.offline
        ? createViewOptions(summaryConfigurations?.sharedConfigurations?.filter(o => (o.sectionCount ?? 0) < 2 || !isSystemBalance),
            PageConfigurationAccessEnum.SharedReadOnly,
            defaultConfigID,
            true
            )
        : new Array<PageConfigurationSelectOption>();
        }, [
        summaryConfigurations,
        offlineState.offline,
        isSystemBalance,
        defaultConfigID
    ]);


    const privateConfigurations = useMemo(() => { 
        return summaryConfigurations && !offlineState.offline
            ? createViewOptions(summaryConfigurations?.privateConfigurations?.filter(o => (o.sectionCount ?? 0) < 2 || !isSystemBalance),
                PageConfigurationAccessEnum.Private,
                defaultConfigID,
            false
            )
            : new Array<PageConfigurationSelectOption>();
    }, [
        summaryConfigurations,
        offlineState.offline,
        isSystemBalance,
        defaultConfigID
    ]);

    const optionGroups = useMemo(() => {

        const _optionGroups = [{
            heading: t("summaryListing.viewList.sharedViewsHeading"),
            options: isSystemBalance ? sharedConfigurations.filter(o => (o.sectionCount ?? 0) < 2) : sharedConfigurations
        }, {
            heading: t("summaryListing.viewList.sharedReadOnlyViewsHeading"),
            options: isSystemBalance ? sharedReadOnlyConfigurations.filter(o => (o.sectionCount ?? 0) < 2) : sharedReadOnlyConfigurations

        }, {
            heading: t("summaryListing.viewList.privateViewsHeading"),
            options: isSystemBalance ? privateConfigurations.filter(o => (o.sectionCount ?? 0) < 2) : privateConfigurations            
        }];

        if (isSystemBalance && summaryConfigurations) {

            const unavailableConfigurations = (summaryConfigurations.sharedConfigurations?.concat(summaryConfigurations.privateConfigurations ?? []) ?? [])
                                        .filter(o => (o.sectionCount ?? 0) > 1);

            if (unavailableConfigurations.length) {
                // User Story 53209: Make Code Changes per GAS Report
                const options = createViewOptions(unavailableConfigurations,
                        PageConfigurationAccessEnum.Shared,
                        defaultConfigID,
                    false)
                    .concat(createViewOptions(unavailableConfigurations,
                        PageConfigurationAccessEnum.SharedReadOnly,
                        defaultConfigID,
                    false))
                    .concat(createViewOptions(unavailableConfigurations,
                        PageConfigurationAccessEnum.Private,
                        defaultConfigID,
                    false));

                _optionGroups.push({
                    heading: t("summaryListing.viewList.currentlyUnavailable"),
                    options: Enumerable.from(options).orderBy(o => o.label).toArray()
                });
            }
        }
        return _optionGroups;

    },[
        isSystemBalance,
        sharedConfigurations,
        sharedReadOnlyConfigurations,
        privateConfigurations,
        summaryConfigurations,
        t,
        defaultConfigID
    ]);
    
    const deviceViewID = useMemo(() => {
        return defaultViews?.find(o => o.objectType === eLynxObjectTypeEnum.Device && 
            o.user?.id === userState.user?.id)?.viewID ?? 
                defaultViews?.find(o => o.objectType === eLynxObjectTypeEnum.Device && 
                    !o.user)?.viewID ??
                    UIViewEnum.Device_Details;
    }, [
        defaultViews,
        userState.user?.id
    ]);

    // const data = useMemo(() =>  {

    //     const route = routesByViewID.get(deviceViewID);

    //     return createGridData(
    //         summaryData,
    //         isPhone,
    //         groupID,
    //         //isPhone ? false : userState.userSettings?.summaryListingDisplayFunctionIcons ?? false,
    //         isPhone ? false : state.summaryListingDisplayFunctionIcons,
    //         sortType,
    //         isPhone && !route.allowMobile ? UIViewEnum.Device_Details : deviceViewID,
    //         dense ?? false
    //     );
    // }, [
    //     summaryData,
    //     isPhone,
    //     groupID,
    //     //userState.userSettings?.summaryListingDisplayFunctionIcons,
    //     state.summaryListingDisplayFunctionIcons,
    //     sortType,
    //     deviceViewID,
    //     dense
    // ]);


    const groupedData = useMemo(() => {

        const showShortcuts = state.historicalData || state.graph ||
                                state.marketingReport ||
                                state.plungerAnalysis ||
                                state.gaugeSheet ||
                                state.dynacard ||
                                state.addMemo ||
                                state.mapIt;

        return state.groupByID !== -1 ? 
            groupDataByAttribute(summaryData, 
                                state.groupByID,
                                groupByAttributeData,
                                groupID, 
                                isPhone, 
                                isPhone ? 
                                    false : 
                                    //userState.userSettings?.summaryListingDisplayFunctionIcons ?? false,
                                    //state.summaryListingDisplayFunctionIcons,
                                    showShortcuts,
                                    state.filterText,
                                    sortType,
                                    deviceViewID,
                                    dense ?? false,
                                    state.historicalData,
                                    state.graph,
                                    state.marketingReport,
                                    state.plungerAnalysis,
                                    state.gaugeSheet,
                                    state.dynacard,
                                    state.addMemo,
                                    state.mapIt            
                                    ) :
            [];
    },
    [
        state.groupByID,
        isPhone,
        summaryData,
        groupByAttributeData,
        groupID,
        //userState.userSettings?.summaryListingDisplayFunctionIcons,
        //state.summaryListingDisplayFunctionIcons,
        state.filterText,
        sortType,
        deviceViewID,
        dense,
        state.historicalData,
        state.graph,
        state.marketingReport,
        state.plungerAnalysis,
        state.gaugeSheet,
        state.dynacard,
        state.addMemo,
        state.mapIt            
    ]);

    const allChecked = state.historicalData && state.graph &&  state.marketingReport &&
                                                state.plungerAnalysis && state.gaugeSheet &&
                                                state.dynacard && state.addMemo && state.mapIt;

    const shortcuts = [state.historicalData, state.graph, state.marketingReport,
                                                state.plungerAnalysis, state.gaugeSheet,
                                                state.dynacard, state.addMemo, state.mapIt];

    const indeterminate = !allChecked ? shortcuts.some(o => o) : allChecked ? shortcuts.some(o => !o) : false;


    const pageCount = Math.trunc(groupedData.length / pageSize) + ((groupedData.length % pageSize) ? 1 : 0);

    //console.log(`data len: ${groupedData.length} pageCount: ${pageCount}`)

    const paginatedData = Enumerable.from(groupedData)
                        .skip((state.page - 1) * pageSize)
                        .take(pageSize)
                        .toArray()

    const handleViewChange = (value: number) => {

        if (isSystemBalance && (optionGroups.flatMap(o => o.options).find(o => o.value === value)?.sectionCount ?? 0) > 1) {

            confirm({
                dialogTitle: t("summaryListing.unavailableConfiguration"),
                confirmationText: t("summaryListing.unavailableConfigurationText"),
                yesButtonText: t("buttons.okay"),
                showConfirmationText: true,
                showYesButton: true,
                showNoButton: false,
                showCloseButton: false
            });

        } else {
            setConfigurationID(value ?? state.configurationID);
        }
    };

    async function onCreateConfiguration() {
        handleEditorResult(await configurationDialog.open({
            pageConfigurationType: PageConfigurationTypeEnum.SummaryListing_RTS
        }));
    }

    async function onDuplicateConfiguration(fromEdit?: boolean) {


        if (!fromEdit) {

            try {

                const deprecatedColumns = await hasDeprecatedColumns(groupID, state.configurationID);

                if (deprecatedColumns) {
        
                    const result = await _showDeprecationConfirmationPrompt(true);
            
                    if (result === "yes") {
                        // continue editing
                        handleEditorResult(await configurationDialog.open({
                            configurationID: state.configurationID ?? -1,
                            pageConfigurationType: PageConfigurationTypeEnum.SummaryListing_RTS,
                            duplicate: true
                        }));

                    } 
                } else {
                    handleEditorResult(await configurationDialog.open({
                        configurationID: state.configurationID ?? -1,
                        pageConfigurationType: PageConfigurationTypeEnum.SummaryListing_RTS,
                        duplicate: true
                    }));
    
                }

            } catch (error) {
                const err = error as RestError;

                setAppNotificationState({
                    notificationType: AppNotificationTypeEnum.Error,
                    message: err.message,
                    show: true,
                    error: err
                });
            }

        } else {

            handleEditorResult(await configurationDialog.open({
                configurationID: state.configurationID ?? -1,
                pageConfigurationType: PageConfigurationTypeEnum.SummaryListing_RTS,
                duplicate: true
            }));
        }
    }

    function handleEditorResult(result: PageConfigurationEditorDialogResult) {
        if (result.reason === "saveAsNew" || result.reason === "saveChanges") {
            setConfigurationID(result.values?.configurationID as number);
            refetchConfigurations();
            setRefreshing(true);
            refetch();
        }
    }

    async function _showDeprecationConfirmationPrompt(duplicate: boolean) {
        return confirm({
            dialogTitle: duplicate ?  
                t("summaryListingConfiguration.deprecationConfirmationDuplicate.title") : 
                t("summaryListingConfiguration.deprecationConfirmationEdit.title"),
            confirmationText: duplicate ?  
                    t("summaryListingConfiguration.deprecationConfirmationDuplicate.text") : 
                    t("summaryListingConfiguration.deprecationConfirmationEdit.text"),
            yesButtonText: t("configuration.editOrDuplicateConfirmation.yesButtonText"),
            noButtonText: t("summaryListingConfiguration.cancelConfirmationNoButtonText"),
            showConfirmationText: true,
            showYesButton: true,
            showNoButton: true,
            showCloseButton: true
        });
    }
    
    async function onEditConfiguration() {

        // if the currently selected configuration is shared or shared readonly, then
        // display a dialog warning that changes will affect other users
        if (state.configurationID) {


            try {

                const configuration = (summaryConfigurations?.privateConfigurations ?? [])
                .concat(summaryConfigurations?.sharedConfigurations ?? [])
                .find(o => o.id === state.configurationID);
    

                const deprecatedColumns = await hasDeprecatedColumns(groupID, state.configurationID);

                if (deprecatedColumns) {
        
                    const result = await _showDeprecationConfirmationPrompt(false);
            
                    if (result === "yes") {
                        await onDuplicateConfiguration(true);
                    } else if (result === "no") {
    
                        // // continue editing
                        // handleEditorResult(await configurationDialog.open({
                        //     configurationID: state.configurationID ?? -1,
                        //     pageConfigurationType: PageConfigurationTypeEnum.SummaryListing_RTS
                        // }));
    
                    } // else do nothing
                } else if (configuration?.access === PageConfigurationAccessEnum.Shared ||
                    configuration?.access === PageConfigurationAccessEnum.SharedReadOnly) {

                   const result = await confirm({
                       dialogTitle: t("configuration.editOrDuplicateConfirmation.title"),
                       confirmationText: t("configuration.editOrDuplicateConfirmation.confirmationText", { configurationType: t("configuration.types.pageConfiguration") }),
                       yesButtonText: t("configuration.editOrDuplicateConfirmation.yesButtonText"),
                       noButtonText: t("configuration.editOrDuplicateConfirmation.noButtonText"),
                       showConfirmationText: true,
                       showYesButton: true,
                       showNoButton: true,
                       showCloseButton: true
                   });
   
                   if (result === "yes") {
                       // duplicate
                       await onDuplicateConfiguration(true);
                   } else if (result === "no") {
   
                       // continue editing
                       handleEditorResult(await configurationDialog.open({
                           configurationID: state.configurationID ?? -1,
                           pageConfigurationType: PageConfigurationTypeEnum.SummaryListing_RTS
                       }));
   
                   } // else do nothing

               } else {

                   handleEditorResult(await configurationDialog.open({
                       configurationID: state.configurationID ?? -1,
                       pageConfigurationType: PageConfigurationTypeEnum.SummaryListing_RTS
                   }));
   
               }

            } catch (error) {
                const err = error as RestError;

                setAppNotificationState({
                    notificationType: AppNotificationTypeEnum.Error,
                    message: err.message,
                    show: true,
                    error: err
                });
            }
        }

    }

    const onRenameConfiguration = async () => {

        const configuration = (summaryConfigurations?.privateConfigurations ?? [])
                                .concat(summaryConfigurations?.sharedConfigurations ?? [])
                                .find(o => o.id === state.configurationID);

        if (configuration?.access) {

            if (configuration?.access === PageConfigurationAccessEnum.Shared ||
                configuration?.access === PageConfigurationAccessEnum.SharedReadOnly) {

                const response = await confirm(RENAME_CONFIGURATION_DIALOG_PARAMS);
                if (response !== "yes") {
                    return;
                }
            }

            const result = await renameConfigurationDialog.open({
                configurationName: configuration?.name as string,
                configurationType: t("configuration.types.pageConfiguration")
            });

            if (result.reason === 'ok' && result.values) {

                try {

                    renameConfiguration(result.values.configurationName);

                } catch (error) {

                    const err = error as RestError;

                    setAppNotificationState({
                        notificationType: AppNotificationTypeEnum.Error,
                        message: err.message,
                        show: true,
                        error: err
                    });
                }
            }
        }
    };

    const onDeleteConfiguration = async () => {

        const configuration = (summaryConfigurations?.privateConfigurations ?? [])
                                .concat(summaryConfigurations?.sharedConfigurations ?? [])
                                .find(o => o.id === state.configurationID);

        if (configuration) {
            const response = await confirm({
                dialogTitle: t('configuration.deleteConfirmation.title', {
                    configurationType: t("configuration.types.pageConfiguration"),
                    configurationName: configuration.name
                }),
                confirmationText: t('configuration.deleteConfirmation.confirmationText'),
                yesButtonText: t("configuration.deleteConfirmation.yesButtonText"),
                noButtonText: t("configuration.deleteConfirmation.noButtonText"),
                showConfirmationText: true,
                showYesButton: true,
                showNoButton: true
            });


            if (response === "yes") {

                try {

                    deleteConfiguration({
                        configurationID: state.configurationID as number,
                        pageConfigurationType: PageConfigurationTypeEnum.SummaryListing_RTS,
                   });

                } catch (error) {

                    const err = error as RestError;

                    setAppNotificationState({
                        notificationType: AppNotificationTypeEnum.Error,
                        message: err.message,
                        show: true,
                        error: err
                    });
                }
            }
        }
    };
    
    const onDefaultConfiguration = async () => {

        const configuration = (summaryConfigurations?.privateConfigurations ?? [])
                                .concat(summaryConfigurations?.sharedConfigurations ?? [])
                                .find(o => o.id === state.configurationID);

        if (configuration) {

            //const hasWritePermission = configuration?.userHasPermission(writePermission);     
            const hasWritePermission = hasPermission(groupViewState?.accessPermissions ?? [], writePermission);                   
            
            try {

                if (configuration.access === PageConfigurationAccessEnum.Private || !hasWritePermission) {

                    // for some odd reason, product wants to group by ID to be applied to the default configuration.
                    // However, this can only be done if the user has write permissions
                    if (hasWritePermission) {                    
                        const updatedConfiguration = {...configurationModel,
                            groupByAttributeID: state.groupByID
                        } as PageConfigurationModel4;

                    
                        saveConfiguration(updatedConfiguration);
                    }
                   

                    setDefaultConfiguration({
                        configurationID: configuration.id,
                        defaultForUser: true,
                        defaultForGroup: false,
                        defaultForAllUsers: false
                    });


                } else {
                    const defaultOptions = await PageConfigurationService.getPageConfigurationDefaultOptions(groupID, configuration.id as number);
                
                    const result = await defaultConfigurationDialog.open({
                        configurationName: configuration.name ?? '',
                        groupName: summaryConfigurations?.groupDescription ?? '',
                        forCurrentUser: defaultOptions.defaultForUser ?? false,
                        forUsersWithoutDefault: defaultOptions.defaultForGroup ?? false,
                        forAllUsers: defaultOptions.defaultForAllUsers ?? false,
                        configurationID: configuration.id as number
                    });

                    if (result.reason === 'ok' && result.values) {

                        // for some odd reason, product wants to group by ID to be applied to the default configuration
                        const updatedConfiguration = {...configurationModel,
                            groupByAttributeID: state.groupByID
                        } as PageConfigurationModel4;

                        saveConfiguration(updatedConfiguration);

                        setDefaultConfiguration({
                            configurationID: configuration.id,
                            defaultForUser: result.values.forCurrentUser,
                            defaultForGroup: result.values.forUsersWithoutDefault,
                            defaultForAllUsers: result.values.forAllUsers
                        });
                    }
                }
            } catch (error) {

                const err = error as RestError;

                setAppNotificationState({
                    notificationType: AppNotificationTypeEnum.Error,
                    message: err.message,
                    show: true,
                    error: err
                });
            }
        }
    };

    async function onModifyPermissions() {


        const configuration = (summaryConfigurations?.privateConfigurations ?? [])
                                .concat(summaryConfigurations?.sharedConfigurations ?? [])
                                .find(o => o.id === state.configurationID);

        if (configuration) {

        const userHasWriteAccessToConfiguration = ((configuration?.access === PageConfigurationAccessEnum.Private) ||
             configuration?.userHasPermission(writePermission)) ?? false;


            const userHasModifyPermission = configuration?.userHasPermission(writePermission); 
            
            if (configuration?.access === PageConfigurationAccessEnum.Shared ||
                configuration?.access === PageConfigurationAccessEnum.SharedReadOnly) {

                const response = await confirm(ACCESS_LEVEL_MODIFY_CONFIRMATION_PARAMS);
                if (response !== "yes") {
                    return;
                }
            }

            const result = await accessLevelDialog.open({
                access: configuration?.access === PageConfigurationAccessEnum.Private ? 
                                        AccessType.Private : 
                                        configuration?.access === PageConfigurationAccessEnum.Shared ?
                                        AccessType.Shared : 
                                        configuration?.access === PageConfigurationAccessEnum.SharedReadOnly ?
                                        AccessType.SharedReadOnly : 
                                        AccessType.None,
                configurationType: t("configuration.types.pageConfiguration"),
                hasGroupWrite: userHasModifyPermission,
                hasConfigWrite: userHasWriteAccessToConfiguration
            });

            if (result.reason === 'ok' && result.values?.access) {

                try {

                    modifyAccess(result.values.access);

                } catch (error) {

                    const err = error as RestError;

                    setAppNotificationState({
                        notificationType: AppNotificationTypeEnum.Error,
                        message: err.message,
                        show: true,
                        error: err
                    });
                }
            }
        }

    }

    function executeAction(action: BaseActionEnum | string) {
        switch (action) {
            case BaseActionEnum.Refresh:
                setRefreshing(true);
                refetch();
                break;

            case BaseActionEnum.Edit:
                onEditConfiguration();
                break;

            case BaseActionEnum.SetAsDefault:
                onDefaultConfiguration();
                break;

            case BaseActionEnum.CreateNew:
                onCreateConfiguration();
                break;

            case BaseActionEnum.Duplicate:
                onDuplicateConfiguration();
                break;

            case BaseActionEnum.ModifyPermissions:
                onModifyPermissions();
                break;

            case BaseActionEnum.Delete:
                onDeleteConfiguration();
                break;

            case BaseActionEnum.Rename:
                onRenameConfiguration();
                break;
    
                
        }
    }

    function getExportParameters() {

        const sortedColumn = state.gridApi?.getColumnState().find(o => o.sort);

        const index = sortedColumn?.sortIndex ?? -1;

        const isSortAscending = sortedColumn
            ? state.gridApi?.getColumn(sortedColumn?.colId)?.getSort() === 'asc'
            : false;

        return {

            configurationID: state.configurationID,
            sortByRecentMemo: index === 0,
            sortAscending: isSortAscending ?? false,
            //sortColumnIndex: userState.userSettings?.summaryListingDisplayFunctionIcons ? 
            sortColumnIndex: state.summaryListingDisplayFunctionIcons ? 
                            index > 1 ? 
                                index - 2 : 
                                0 :
                            index > 0 ? 
                                index - 1 :
                                0,
            isSystemBalance: isSystemBalance,
            useCustomOrdering: sortType === SummaryListingSortTypeEnum.Custom,
        } as ISummaryListingExportParams;
    }
    
    function onResetOptions() {

        resetOptions(userSettings!);
    }

    function onGroupByChange(value: string) {
        setGroupByID(+value);
    }

    function onPageChange(event: React.ChangeEvent<unknown>, page: number) {

        setPage(page);

    }

    // function onDisplayShortcuts(checked: boolean) {

    //     setFunctionIcons(checked);
                
    //     // the grid data must be cleared to cause the ag grid to correctly render the columns
    //     setData([]);

    //     setTimeout(() => {
    //         state.gridApi?.autoSizeAllColumns({
    //                     skipHeader: true,
    //                     //scaleUpToFitGridWidth: true,
    //                 });

    //     }, 1000);
    // }
    function onDisplayShortcuts() {

        const checked = !allChecked;
        setShowHistoricalDataShortcut(checked);
        setShowGraphShortcut(checked);
        setShowMarketinReportShortcut(checked);
        setShowPlungerAnalysisShortcut(checked);
        setShowGaugesheetShortcut(checked);
        setShowDynacardShortcut(checked);
        setShowAddMemoShortcut(checked);
        setShowMapItShortcut(checked);

        // the grid data must be cleared to cause the ag grid to correctly render the columns
        setData([]);

        setTimeout(() => {
            state.gridApi?.autoSizeAllColumns({
                        skipHeader: true,
                        //scaleUpToFitGridWidth: true,
                    });

        }, 1000);
    }


    function onDisplayShortcut(item: DevicesViewActionEnum, checked: boolean) {

        switch (item) {
            case DevicesViewActionEnum.DisplayHistoricalDataShortcut:
                setShowHistoricalDataShortcut(checked);
                break;

            case DevicesViewActionEnum.DisplayGraphShortcut:
                setShowGraphShortcut(checked);
                break;

            case DevicesViewActionEnum.DisplayMarketingReportShortcut:
                setShowMarketinReportShortcut(checked);
                break;

            case DevicesViewActionEnum.DisplayPlungerAnalysisShortcut:
                setShowPlungerAnalysisShortcut(checked);
                break;

            case DevicesViewActionEnum.DisplayGaugeSheetShortcut:
                setShowGaugesheetShortcut(checked);
                break;

            case DevicesViewActionEnum.DisplayDynacardShortcut:
                setShowDynacardShortcut(checked);
                break;

            case DevicesViewActionEnum.DisplayAddMemoShortcut:
                setShowAddMemoShortcut(checked);
                break;

            case DevicesViewActionEnum.DisplayMapItShortcut:
                setShowMapItShortcut(checked);
                break;

            default:
                break;

        }
                
        // the grid data must be cleared to cause the ag grid to correctly render the columns
        setData([]);

        setTimeout(() => {
            state.gridApi?.autoSizeAllColumns({
                        skipHeader: true,
                        //scaleUpToFitGridWidth: true,
                    });

        }, 1000);
    }


    function onDisplayAlarmsFirst(checked: boolean) {
        setAlarmsAtTop(checked);
    }

    function onDisplayOutletsFirst(checked: boolean) {
        setOutletsFirst(checked);
    }

    function renderGridSection(index: number, o: GridSectionData) {
        return (
            <SectionGrid 
                key={`secgrid-${index}`}
                name={o.caption}
                fitToView={(summaryData?.sectionCount ?? 1) === 1 && state.groupByID === -1}
                columnDefs={o.cols}
                rowData={o.rows}
                isSystemBalance={isSystemBalance}
                filterText={state.filterText}
                configurationID={state.configurationID}
                sortType={sortType}
                disableNavigation={designMode}
                summaryListingOutletsFirst={state.summaryListingOutletsFirst}
                summaryListingAlarmAtTop={state.summaryListingAlarmAtTop}
            />)
    }

    function renderCard(index: number, o: GroupedGridSection ) {
        
        return (
            <Card key={`card-${index}`}
            elevation={4}
            sx={{

                display:"flex",
                flexDirection: "column",
                flexGrow: 0,
                flexShrink: 0,
                padding: theme.spacing(2),
                paddingLeft: isPhone ? 0 : theme.spacing(2),
                paddingRight: isPhone ? 0 : theme.spacing(2),
                paddingTop: 0,
                margin: 1,
                marginLeft: isPhone ? 0 : 1,
                marginRight: isPhone ? 0 : 1,

            }}>

            <Typography variant="h6" sx={{
                padding: 1,
                //backgroundColor: "divider"
            }}>
                {o.title}
            </Typography>


            {o.gridSectionData.map((v, ndx) =>
                renderGridSection(ndx, v)
            )}

        </Card>

        )        
    }

    useEffect(() => {



        const route = routesByViewID.get(deviceViewID);

        const showShortcuts = state.historicalData || state.graph ||
                                state.marketingReport ||
                                state.plungerAnalysis ||
                                state.gaugeSheet ||
                                state.dynacard ||
                                state.addMemo ||
                                state.mapIt;

        const data = createGridData(
            summaryData,
            isPhone,
            groupID,
            //isPhone ? false : userState.userSettings?.summaryListingDisplayFunctionIcons ?? false,
            //isPhone ? false : state.summaryListingDisplayFunctionIcons,
            isPhone ? false : showShortcuts,
            sortType,
            isPhone && !route.allowMobile ? UIViewEnum.Device_Details : deviceViewID,
            dense ?? false,
                state.historicalData,
                state.graph,
                state.marketingReport,
                state.plungerAnalysis,
                state.gaugeSheet,
                state.dynacard,
                state.addMemo,
                state.mapIt            
        );
        setData(data);
    }, [
        summaryData,
        isPhone,
        groupID,
        //userState.userSettings?.summaryListingDisplayFunctionIcons,
        state.summaryListingDisplayFunctionIcons,
        sortType,
        deviceViewID,
        dense,
        setData,
        //state.summaryListingAlarmAtTop,
        //state.summaryListingOutletsFirst
        state.historicalData,
        state.graph,
        state.marketingReport,
        state.plungerAnalysis,
        state.gaugeSheet,
        state.dynacard,
        state.addMemo,
        state.mapIt
    ]);


    useEffect(() => {
        const rowCount = summaryData?.gridData?.flatMap(o => o.rows).length ?? 0;
        setDeviceCount(summaryData?.deviceCount ?? 0);
        setNoData(rowCount === 0);
    }, [summaryData, 
        setDeviceCount, 
        setNoData,
    ]);

    useEffect(() => {
        if (visible) {
            const viewOptionState = state.isOptionsDirty ? 
                ViewOptionStateEnum.Changed : 
                ViewOptionStateEnum.Default;

            setViewOptionState?.(viewOptionState);
        }
    }, [
        visible,
        state.isOptionsDirty,
        setViewOptionState,
        state.isNoData
    ]);

    useEffect(() => {

        if (visible && !dense) {

            let actions: ActionMenuItem[] = [];

            if (summaryConfigurations && !state.isNoData) {
                actions = headerAndActionsV2 ?
                SummaryListingViewUtils.getActionItemsV2(actionItems,
                    isPhone,
                    offlineState.offline,
                    state.configurationID,
                    summaryConfigurations
                )                
                :
                SummaryListingViewUtils.getActionItems(actionItems,
                    isPhone,
                    offlineState.offline,
                    state.configurationID,
                    summaryConfigurations
                );
            }

            setActions(actions);
        }

    }, [
        headerAndActionsV2,
        visible,
        state.isNoData,
        state.configurationID,
        isPhone,
        offlineState.offline,
        summaryConfigurations,
        setActions,
        dense,
    ]);

    useEffect(() => {

        const attributeOptions = getAttributeOptions(summaryData?.groupByAttributes);
        if (!_.isEqual(attributeOptions, state.attributeOptions)) {
            setAttributeOptions(attributeOptions);
        }

    }, [
        summaryData,
        state.attributeOptions,
        setAttributeOptions
    ]);

    useEffect(() => {
       
        const groupByAttribute = state.attributeOptions.find(o => o.aid === configurationModel?.groupByAttributeID);
        setGroupByID(configurationModel?.id === state.defaultConfigurationID && 
                    groupByAttribute &&
                    configurationModel?.groupByAttributeID ? 
                        configurationModel.groupByAttributeID : 
                    -1);

    }, [
        configurationModel,
        setGroupByID,
        state.attributeOptions,
        state.defaultConfigurationID
    ]);

    // Update the configuration id
    useEffect(() => {
        if (!offlineState.offline && !dense) {

            setDefaultConfigurationID(defaultConfigID);
           
            const configID = (routeContext.viewID === UIViewEnum.Group_SummaryListing_Devices ? routeContext.state?.configurationID : undefined);
            const navSrc = routeContext.navSrc;

            if (configID) {
                setConfigurationID(configID);
            } else if (navSrc !== NavigationSource.Tab && defaultConfigID > 0) {
                setConfigurationID(defaultConfigID);
            } else if (navSrc === NavigationSource.Tab) {
                setConfigurationID(cid => cid > -1 ? cid : defaultConfigID);
            }
        }
    }, [
        defaultConfigID,
        // This does not work correctly. Need entire routeContext.
        // routeContext.viewID,
        // routeContext.state?.configurationID,
        // routeContext.navSrc,
        routeContext,
        offlineState.offline,
        setDefaultConfigurationID,
        setConfigurationID,
        dense
    ]);


    useEffect(() => {
        if (!offlineState.offline && dense && configurationID) {
            setDefaultConfigurationID(configurationID);
            setConfigurationID(configurationID);
        }
    },[
        configurationID,
        dense,
        setConfigurationID,
        setDefaultConfigurationID,
        offlineState.offline
    ])

    // Keep the favorite context in sync with the selected configuration
    useEffect(() => {

        if (!dense && routeContext.viewID === UIViewEnum.Group_SummaryListing_Devices) {

            let configuration = summaryConfigurations?.privateConfigurations?.find(c => c.id === state.configurationID);

            if (!configuration) {
                configuration = summaryConfigurations?.sharedConfigurations?.find(c => c.id === state.configurationID);
            }

            if(configuration) {
                //  console.log(`#### Summary Listing - Devices updateFavoriteContext`);

                updateFavoriteContext({
                    // Only include configuration id and name if something other than the default is selected
                    configurationID: summaryConfigurations?.defaultPageConfigurationId !== state.configurationID
                        ? state.configurationID > -1 ? state.configurationID : undefined
                        : undefined,
                    configurationName: summaryConfigurations?.defaultPageConfigurationId !== state.configurationID
                        ? configuration?.name
                        : undefined
                });
            }
        }
    }, [
        state.configurationID,
        summaryConfigurations?.privateConfigurations,
        summaryConfigurations?.sharedConfigurations,
        summaryConfigurations?.defaultPageConfigurationId,
        routeContext.viewID,
        updateFavoriteContext,
        dense
    ]);

    useEffect(() => {

        if (visible && headerAndActionsV2 && !dense) {

            //if (!_.isEqual(optionGroups, configurationsContextState.configurations)) {

                //console.log(`devicesview settting configurations: ${optionGroups.length} and viewID: ${routeContext.viewID}`)
                setContextConfigurations(optionGroups);
            //}
        }
    }, [
        visible,
        dense,
        headerAndActionsV2,
        optionGroups,
        configurationsContextState.configurations,
        setContextConfigurations,
        routeContext.contextObject,
        routeContext.viewID
    ]);

    useEffect(() => {
        if (visible && headerAndActionsV2 && !dense) {
            
            setContextConfigurationID(state.configurationID);
        }

    }, [
        visible,
        headerAndActionsV2,
        dense,
        state.configurationID,
        setContextConfigurationID,
        configurationsContextState.configurations,
        routeContext.groupID
    ]);

    useEffect(() => {

        async function onConfigurationChanged(value: number) {


            if (isSystemBalance && (optionGroups.flatMap(o => o.options).find(o => o.value === value)?.sectionCount ?? 0) > 1) {

                await confirm({
                    dialogTitle: t("summaryListing.unavailableConfiguration"),
                    confirmationText: t("summaryListing.unavailableConfigurationText"),
                    yesButtonText: t("buttons.okay"),
                    showConfirmationText: true,
                    showYesButton: true,
                    showNoButton: false,
                    showCloseButton: false
                });

            } else {
                setConfigurationID(value);
            }
        }

        if (visible && headerAndActionsV2 && !dense) {
            const token = subscribe(CONFIGURATION_CHANGED_TOPIC, (message, data) => onConfigurationChanged((data as ConfigurationChangedMsg).configurationID));
    
            return () => {
                if (token) {
                    unsubscribe(token);
                }
            }
        }
    }, [
        visible,
        headerAndActionsV2,
        dense,
        summaryConfigurations,
        isSystemBalance,
        setConfigurationID,
        confirm,
        optionGroups,
        t
    ]);

    useEffect(() => {
        if (userSettings) {
            setAlarmsAtTop(userSettings.summaryListingAlarmAtTop);
            setOutletsFirst(userSettings.summaryListingOutletsFirst);
            //setFunctionIcons(userSettings.summaryListingDisplayFunctionIcons);
            setShowHistoricalDataShortcut(((userSettings.uiShortcutMask ?? 0) & UIShortcutEnum.HistoricalData) > 0 );
            setShowGraphShortcut(((userSettings.uiShortcutMask ?? 0) & UIShortcutEnum.Graph) > 0 );
            setShowMarketinReportShortcut(((userSettings.uiShortcutMask ?? 0) & UIShortcutEnum.MarketingReport) > 0 );
            setShowPlungerAnalysisShortcut(((userSettings.uiShortcutMask ?? 0) & UIShortcutEnum.PlungerAnalysis) > 0 );
            setShowGaugesheetShortcut(((userSettings.uiShortcutMask ?? 0) & UIShortcutEnum.GaugeSheet) > 0 );
            setShowDynacardShortcut(((userSettings.uiShortcutMask ?? 0) & UIShortcutEnum.Dynacard) > 0 );
            setShowAddMemoShortcut(((userSettings.uiShortcutMask ?? 0) & UIShortcutEnum.AddMemo) > 0 );
            setShowMapItShortcut(((userSettings.uiShortcutMask ?? 0) & UIShortcutEnum.MapIt) > 0 );
        }

    },[
        userSettings,
        setAlarmsAtTop,
        setOutletsFirst,
        setFunctionIcons,
        setShowHistoricalDataShortcut,
        setShowGraphShortcut,
        setShowMarketinReportShortcut,
        setShowPlungerAnalysisShortcut,
        setShowGaugesheetShortcut,
        setShowDynacardShortcut,
        setShowAddMemoShortcut,
        setShowMapItShortcut

    ]);

    // useEffect(() => {

    //     if (state.gridApi) {
    //         state.gridApi.resetColumnState();
    //         state.gridApi.redrawRows(); 
    //     }

    // }, [
    //     data,
    //     state.gridApi
    // ]);
    

    return (
        <>
            {visible &&
                <Box sx={{
                    display: "flex",
                    flexDirection: "column",
                    flexGrow: 1,
                    flexShrink: 1,
                    overflow: "hidden",
                }}>

                    {!offlineState.offline && !dense &&
                    <>
                        <PageConfigurationEditorDialog {...bindPageConfigurationEditorDialog(configurationDialog)} />          
                        <RenameConfigurationDialog {...bindRenameConfigurationDialog(renameConfigurationDialog)} />       
                        <DefaultConfigurationDialog {...bindDefaultConfigurationDialog(defaultConfigurationDialog)} />
                        <AccessLevelDialog {...bindAccessLevelDialog(accessLevelDialog)} />                        
                    </>                        
                    }

                    <LoadingZone isLoading={status === "loading" || state.refreshing}>
                        <ContentContainer>
                            <ContentSection>
                                <Stack direction='column'
                                    sx={{
                                        flex: "1 1 auto",
                                        overflowY: (summaryData?.sectionCount ?? 0) > 1 || state.groupByID !== -1 ? 'auto' : 'hidden'
                                    }}
                                >
                            
                                    {hasGridDataToDisplay ?
                                        state.groupByID !== -1 ?  
                                        paginatedData.map((o, index) =>
                                                renderCard(index, o)
                                            )
                                        :
                                        data.filter(o => o.rows.length > 0).map((o, index) => 
                                            renderGridSection(index, o)
                                        )
                                        :
                                    state.confiurationError ?
                                        
                                        <NoData 
                                            severity="error"
                                            title={t("summaryListing.configurationError")}
                                            text={t("summaryListing.configurationErrorText", {name: configurationName})} 
                                        />
                                        :

                                    (state.isNoData || !hasGridDataToDisplay) && (status === "success" || status === "error") &&
                                        <NoData text={data.flatMap(o => o.rows).length === 0 ? noDataMessage : noDataFilterMessage} />
                                    }

                                </Stack>

                                {hasGridDataToDisplay &&
                                    state.groupByID !== -1  && pageCount > 1 ?  
                                    <Pagination count={pageCount} 
                                        sx={{
                                            display: 'flex',
                                            justifyContent: 'center',
                                            backgroundColor: "custom.background.2",
                                            padding: 1

                                        }}
                                        siblingCount= {isPhone ? 0 : undefined}
                                        page={state.page}
                                        showFirstButton 
                                        showLastButton
                                        onChange={onPageChange}
                                         /> : 
                                    null
                                }
                                
                            </ContentSection>

                            {!dense && <OptionsSideSheet onReset={onResetOptions}>
                                {!offlineState.offline && (!headerAndActionsV2 || isPhone) &&
                                    <SideSheetGroupedSelect
                                        value={state.configurationID}
                                        label={t("summaryListing.viewList.placeholder")}
                                        optionGroups={optionGroups}
                                        onChange={handleViewChange} />
                                }

                                <SideSheetFilterInput
                                    label={t("summaryListing.findDevice")}
                                    value={state.filterText}
                                    onChange={(value) => {
                                        setFilterText(value);
                                    }}
                                />


                                { 
                                !isSystemBalance && (summaryData?.sectionCount ?? 0) > 0 && state.attributeOptions.length > 0 &&
                                    <SideSheetSelect
                                        label={t("summaryListing.groupBy")}
                                        value={state.groupByID?.toString() ?? "-1"} 
                                        onChange={onGroupByChange}>
                                    <MenuItem value="-1">
                                        {t("summaryListing.hardwareType")}
                                    </MenuItem>
                                    {
                                        state.attributeOptions.map((o, index) => (
                                            <MenuItem key={`attr-${index}`}
                                                value={o.aid}
                                            >
                                                {o.an}
                                            </MenuItem>
                                        ))
                                    }
                                </SideSheetSelect>
                                }

                                {headerAndActionsV2 ? 
                                <>
                                <Typography sx={{
                                    fontWeight: 600
                                }}>
                                    Display
                                </Typography>
                                {!isPhone && 
                                    // <SideSheetCheckbox
                                    //     //size="small"
                                    //     //name="functionIcons"
                                    //     label={<Stack direction='row' sx={{
                                    //         color: theme.palette.text.secondary
                                    //     }}>
                                    //         <Typography>{t('profile.shortcuts')}</Typography>
                                    //         <FontAwesomeIcon icon={faChartGantt}  style={{  marginLeft: "8px", height: "20px", width: "20px" }}/>
                                    //         <i className='sw sw-money-graph fa-lg half-right' style={{ marginLeft: "8px", height: '20px', width: '20px' }} />
                                    //         <i className='sw sw-tank fa-lg half-right' style={{ marginLeft: "8px", height: '20px', width: '20px' }} /> 
                                    //     </Stack>}
                                    //     checked={state.summaryListingDisplayFunctionIcons}
                                    //     onChange={(checked) => onDisplayShortcuts(checked)}
                                    // />



                                    <Stack sx={{flexDirection: 'column'}}>


                        {/* <FormCheckbox
                            name="functionIcons"
                            label={t('profile.displayFunctionIcons')}
                            sx={{width: '90%'}}
                            
                        /> */}

                        <FormControlLabel
                            label={t('profile.displayFunctionIcons')}
                            sx={{
                                '& .MuiFormControlLabel-label': {
                                    //fontWeight: 'bold',
                                    color: theme.palette.text.secondary
                                }}}
                            control={
                                <Checkbox 
                                
                                    checked={allChecked} 
                                    indeterminate={indeterminate}
                                    onChange={() => onDisplayShortcuts()} />
                            }
                        />



                        <Stack direction='column' sx={{ml: 3}}>

                            <SideSheetCheckbox
                                label={t('profile.historicalData')}
                                sx={{color: theme.palette.text.secondary}}
                                checked={state.historicalData}
                                onChange={(checked) => onDisplayShortcut(DevicesViewActionEnum.DisplayHistoricalDataShortcut, checked)}
                            />                            

                            <SideSheetCheckbox
                                label={t('profile.graphing')}
                                sx={{color: theme.palette.text.secondary}}
                                checked={state.graph}
                                onChange={(checked) => onDisplayShortcut(DevicesViewActionEnum.DisplayGraphShortcut, checked)}
                            />                            

                            <SideSheetCheckbox
                                label={t('profile.marketingReport')}
                                sx={{color: theme.palette.text.secondary}}
                                checked={state.marketingReport}
                                onChange={(checked) => onDisplayShortcut(DevicesViewActionEnum.DisplayMarketingReportShortcut, checked)}
                            />                            

                            <SideSheetCheckbox
                                label={t('profile.plungerAnalysis')}
                                sx={{color: theme.palette.text.secondary}}
                                checked={state.plungerAnalysis}
                                onChange={(checked) => onDisplayShortcut(DevicesViewActionEnum.DisplayPlungerAnalysisShortcut, checked)}
                            />                            

                            <SideSheetCheckbox
                                label={t('profile.gaugeSheet')}
                                sx={{color: theme.palette.text.secondary}}
                                checked={state.gaugeSheet}
                                onChange={(checked) => onDisplayShortcut(DevicesViewActionEnum.DisplayGaugeSheetShortcut, checked)}
                            />                            

                            <SideSheetCheckbox
                                label={t('profile.dynacard')}
                                sx={{color: theme.palette.text.secondary}}
                                checked={state.dynacard}
                                onChange={(checked) => onDisplayShortcut(DevicesViewActionEnum.DisplayDynacardShortcut, checked)}
                            />                            
                            <SideSheetCheckbox
                                label={t('profile.addMemo')}
                                sx={{color: theme.palette.text.secondary}}
                                checked={state.addMemo}
                                onChange={(checked) => onDisplayShortcut(DevicesViewActionEnum.DisplayAddMemoShortcut, checked)}
                            />                            
                            <SideSheetCheckbox
                                label={t('profile.mapIt')}
                                sx={{color: theme.palette.text.secondary}}
                                checked={state.mapIt}
                                onChange={(checked) => onDisplayShortcut(DevicesViewActionEnum.DisplayMapItShortcut, checked)}
                            />                            

                        </Stack>

    {/* historicalData?: boolean | undefined;
    graph?: boolean | undefined;
    marketingReport?: boolean | undefined;
    plungerAnalysis?: boolean | undefined;
    gaugeSheet?: boolean | undefined;
    dynacard?: boolean | undefined;
    addMemo?: boolean | undefined;
    mapIt?: boolean | undefined; */}



                    </Stack>













                                }
                                    <SideSheetCheckbox
                                        //name="alarmsAtTop"
                                        sx={{color: theme.palette.text.secondary}}
                                        label={t('profile.alarmFirst', {objectType: "Devices"})}
                                        checked={state.summaryListingAlarmAtTop}
                                        onChange={(checked) => onDisplayAlarmsFirst(checked)}
                                    />

                                    {isSystemBalance && 
                                        <SideSheetCheckbox
                                            sx={{color: theme.palette.text.secondary}}
                                            label={t('profile.outletFirst')}
                                            checked={state.summaryListingOutletsFirst}
                                            onChange={(checked) => onDisplayOutletsFirst(checked)}
                                        />
                                    }
                                    </>
                                    : null
                                }

                            </OptionsSideSheet>
                            }
                        </ContentContainer>
                    </LoadingZone>
                </Box>
            }
        </>
    );

}

export default forwardRef(DevicesView);